import { tv } from "tailwind-variants";
import type { ISymbolImageProps } from "./types";
import { clamp } from "math";
import Icon from "components/icon";

const classes = tv({
  slots: {
    base: "grid items-center justify-center place-center self-center text-center p-1 overflow-hidden aspect-square aspect-square select-none",
    wrapper:
      "grid items-center place-center self-center text-center max-w-[80%] aspect-square aspect-square m-auto justify-center",
    img: "w-full h-full max-w-full max-h-full overflow-hidden",
    symbol: "font-semibold text-center",
  },
  variants: {
    hasValidImage: {
      true: {
        base: 'p-0',
        wrapper: 'p-0 m-0 w-full h-full max-w-full min-w-full min-h-full'
      }
    },
    size: {
      xs: {
        base: "w-5 h-5 p-0",
        img: 'object-cover'
      },
      xs2: {
        base: 'w-[28px] h-[28px]',
        img: 'object-contain'
      },
      xxs2: {
        base: 'w-4 h-4 text-[0.45rem]',
      },
      s: {
        base: "w-8 h-8",
      },
      md: {
        base: "w-10 h-10",
      },
      l: {
        base: "w-16 h-16",
      },
      l2: {
        base: 'w-[86px] h-[86px]'
      },
      lg: {
        base: "w-20 h-20",
      },
      full: {
        base: "w-full h-full",
      },
    },
    fit: {
      contain: {
        img: 'object-contain'
      },
      cover: {
        img: 'object-cover'
      },
      fill: {
        img: 'object-fill'
      }
    },
    color: {
      gray: {
        base: "bg-gray-100 text-gray-400",
      },
    },
    rounded: {
      true: {
        base: "rounded-full",
      },
      'sm': {
        base: 'rounded-[1.12px]'
      }
    },
  },
});

const formatSymbol = (sym: string): string => {
  if (sym.includes(" ")) {
    const words = sym.split(" ");
    if (words.length === 1) return (words[0][0] || "").toUpperCase();
    const [a, b] = words;
    const aUpper = a.toUpperCase();
    const bUpper = b.toUpperCase();
    return `${aUpper[0]}${bUpper[0]}`;
  }
  if (sym.length === 3) {
    return sym.toUpperCase();
  }
  return (sym[0] || "").toUpperCase();
};

export const SymbolImage = defineComponent<ISymbolImageProps>(
  (props) => {


    const sources = computed(() => unref(props.sources) || []);
    const sourceIndex = ref<number>(0);
    const errorCounter = ref<number>(0);
    const currentSource = computed(() => sources.value[sourceIndex.value]);
    const symbol = computed(() => unref(props.symbol));
    const failed = computed(() => errorCounter.value >= sources.value.length);

    const hasValidImage = computed(() => failed.value !== true && !!currentSource.value && sources.value.length > 0);

    const cls = classes({
      color: (props.variant as unknown as any) || "gray",
      rounded: props.rounded ?? false,
      size: (props.size as unknown as any) || "md",
      hasValidImage: !!hasValidImage.value,
      fit: (props.objectFit as any) || 'contain'
    });

    const fallback = computed(() => {
      if (props.fallback) {
        if (typeof props.fallback === "function") return <props.fallback />;
        const el = unref(props.fallback);
        if (el) return el;
      }
      const sym = symbol.value;
      if (sym) {
        return <span class={cls.symbol({})}>{formatSymbol(sym)}</span>;
      }

      return (
        <Icon
          src={async () => import("assets/img/svg/icons/image.svg?raw")}
          size="1rem"
          fill="currentColor"
        />
      );
    });

    watchEffect(() => console.log(sources.value, currentSource.value))
    const setSourceIndex = (idx: number) => {
      if (sources.value.length <= 0) return;
      sourceIndex.value = clamp(idx, 0, sources.value.length - 1);
    };

    const setNextSource = () => {
      setSourceIndex(sourceIndex.value + 1);
    };

    const handleLoad = (source: string) => {
      if (props.onLoad) {
        props.onLoad(source);
      }
    };

    const handleError = (source: string) => {
      if (props.onError) {
        props.onError(source);
      }
      setNextSource();
      errorCounter.value = errorCounter.value + 1;
    };

    return () => {
      return (
        <div
          class={cls.base({})}
          style={{
            aspectRatio: "1 / 1",
          }}
        >
          <span class={cls.wrapper({})}>
            {(failed.value || sources.value.length <= 0) ? (
              fallback.value
            ) : (
              <img
                class={cls.img({})}
                src={currentSource.value}
                onLoad={() => handleLoad(currentSource.value)}
                onError={() => handleError(currentSource.value)}
              />
            )}
          </span>
        </div>
      );
    };
  },
  {
    props: [
      "fallback",
      "symbol",
      "rounded",
      "sources",
      "onLoad",
      "onError",
      "variant",
      "size",
      "objectFit",
    ],
  },
);
